'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { cn } from '@/shared/lib/cn';
import { useConceptsHistory } from '@/features/design/history/hooks/use-concepts-history';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { Star } from 'lucide-react';

interface ConceptThumbnailListProps {
  projectId: string;
  currentConceptId?: string;
}

/**
 * Scrollable list of concept thumbnails for the sidebar.
 * Shows main angle images for each concept in the project.
 */
export function ConceptThumbnailList({
  projectId,
  currentConceptId,
}: ConceptThumbnailListProps) {
  const t = useTranslations('project');
  const router = useRouter();

  // Fetch concepts for this project
  const { data, isLoading } = useConceptsHistory(
    { project_id: projectId },
    'date-desc',
    1,
    50 // Fetch up to 50 concepts for sidebar
  );

  const concepts = useMemo(() => data?.data || [], [data]);

  const handleConceptClick = (conceptId: string) => {
    // SPA navigation - no page reload
    router.push(`/project/${projectId}/concept/${conceptId}`, {
      scroll: false,
    });
  };

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto p-3">
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3] w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (concepts.length === 0) {
    return (
      <div className="h-full p-4 text-center">
        <p className="text-list-item-subtitle text-muted-foreground">{t('noConcepts')}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-3">
      {/* Concept Thumbnails */}
      <div className="grid grid-cols-2 gap-3">
        {concepts.map((concept) => {
          const isActive = concept.id === currentConceptId;
          const hasImage = !!concept.attributes.image_url;
          const isFavorite = concept.attributes.is_favorite;

          return (
            <button
              key={concept.id}
              onClick={() => handleConceptClick(concept.id)}
              className={cn(
                'relative w-full aspect-[4/3] rounded-lg overflow-hidden transition-all',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-muted-foreground/50',
                isActive
                  ? 'ring-1 ring-muted-foreground/50'
                  : 'opacity-50 hover:opacity-75'
              )}
              title={`${t('concept')} #${concept.attributes.project_sequence_number}`}
            >
              {hasImage ? (
                <Image
                  src={concept.attributes.image_url}
                  alt={`${t('concept')} #${concept.attributes.project_sequence_number}`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 50px, 60px"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <span className="text-screen-subtitle text-muted-foreground">
                    #{concept.attributes.project_sequence_number}
                  </span>
                </div>
              )}

              {/* Sequence number badge */}
              <div className="absolute bottom-1 left-1 bg-black/60 text-white text-chip px-1.5 py-0.5 rounded">
                #{concept.attributes.project_sequence_number}
              </div>

              {/* Favorite indicator */}
              {isFavorite && (
                <div className="absolute top-1 right-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                </div>
              )}

              {/* Active indicator overlay */}
              {isActive && (
                <div className="absolute inset-0 bg-primary/10 pointer-events-none" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
